import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { AuthLandingPageService } from '../../providers/auth-landing-page.service';
import { SettingsService } from '../../providers/settings.service';
import { FirebaseAuthService } from '../../providers/firebase-auth.service';
import { UtilityFunctionsService } from '../../providers/utility-functions.service';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { fullscreenPanelAnimation } from '../../providers/animations-list';
import { MatDialog } from '@angular/material/dialog';
import { YoutubeDialogComponent } from 'src/app/web/youtube-dialog/youtube-dialog.component';
import { AuthpopupComponent } from '../authpopup/authpopup.component';
import { discoverLanding } from '../../feature-flags';

@Component({
  selector: 'app-classic-landing',
  templateUrl: './classic-landing.component.html',
  styleUrls: ['./classic-landing.component.scss'],
  animations: [fullscreenPanelAnimation]
})
export class ClassicLandingComponent implements OnInit, AfterViewInit, OnDestroy {
  public isMobileSplashVisible$: Observable<boolean>;
  public mobileSplashConfig$: Observable<{ showSignInForm: boolean; redirectAfterAuth: boolean }>;
  public isMobilePlatform: boolean;
  public detectedMobileDevice: 'android' | 'ios' | null = null;
  public landingMediaBaseUrl = environment.landingMediaBaseUrl;
  public heroMediaCaption = 'Smiling Robot with a Mischievous Twist \u00b7 NatalieDesign \u00b7 Los Angeles, California';
  public heroMediaCaptionUrl = 'https://www.3dway.io/model/Sg4iuU0vg38pzUhIBpDPHk';
  public heroMediaCaptionClass = '';

  @ViewChild('heroMonitorVideo')
  private heroMonitorVideo?: ElementRef<HTMLVideoElement>;

  @ViewChild('heroMobileVideo')
  private heroMobileVideo?: ElementRef<HTMLVideoElement>;

  // The "3DWay references | 3DWay render" switch exists only where /discover does (dev flag).
  public readonly showDiscoverTopbar = discoverLanding;

  constructor(
    public authModalService: AuthLandingPageService,
    private settingsService: SettingsService,
    public firebaseAuth: FirebaseAuthService,
    public utilityService: UtilityFunctionsService,
    private elementRef: ElementRef,
    public dialog: MatDialog
  ) {
    this.isMobileSplashVisible$ = this.authModalService.isMobileSplashVisible$;
    this.mobileSplashConfig$ = this.authModalService.mobileSplashConfig$;
    this.isMobilePlatform = environment.type === 'mobile';
  }

  // This landing is only shown to guests, so Account in the shared topbar means "sign in".
  public onDiscoverAccountClick(): void {
    this.dialog.open(AuthpopupComponent, { data: {}, restoreFocus: false, panelClass: 'webpopup-overlay' });
  }

  ngOnInit(): void {
    this.detectedMobileDevice = this.utilityService.detectMobileDevice();

    if (!this.firebaseAuth.currentUser) {
      this.firebaseAuth.initializeGoogleOneTap(
        () => {
          this.utilityService.showSnackBar('Logged in with Google', 2000);
        },
        (err) => {
          console.log(err);
        }
      );
    }

    if (this.isMobilePlatform) {
      setTimeout(() => {
        this.settingsService.hideSplashScreen();
      }, 1000);
    }
  }

  ngAfterViewInit(): void {
    this.playAllVideos();
    this.setupHeroResizeHandlers();
    this.setupHeroCaptionSync();

    setTimeout(() => {
      this.scrollContainer = this.resolveScrollContainer();
      this.initMobileHeroAnimation();
    });
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.orientationHandler) {
      window.removeEventListener('orientationchange', this.orientationHandler as any);
    }
    if (this.heroUpdateRaf !== null) {
      cancelAnimationFrame(this.heroUpdateRaf);
      this.heroUpdateRaf = null;
    }
    if (this.heroCaptionCleanup) {
      this.heroCaptionCleanup();
      this.heroCaptionCleanup = null;
    }
    this.clearCaptionTimers();
  }

  public scrollContainer: Window | HTMLElement = window;
  private scrollHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private orientationHandler: (() => void) | null = null;
  private heroUpdateRaf: number | null = null;
  private heroCaptionCleanup: (() => void) | null = null;
  private heroCaptionTimers: number[] = [];
  private heroCaptionIndex = 0;
  private heroCaptionLooping = false;
  private heroVideoDuration = 0;
  private readonly heroCaptionLoopLead = 0.6;
  private readonly heroCaptionHalfDuration = 150;
  private readonly heroCaptionFullDuration = 300;
  private readonly heroCaptionCues = [
    {
      start: 0,
      end: 2.2,
      text: 'Smiling Robot with a Mischievous Twist \u00b7 NatalieDesign \u00b7 Los Angeles, California',
      url: 'https://www.3dway.io/model/4kpzQrpXshdVz1JI27gg4U'
    },
    {
      start: 2.2,
      end: 5.2,
      text: 'Cyber Samurai \u00b7 Khoa Nguyen Minh Duong \u00b7 Vietnam',
      url: 'https://www.3dway.io/model/dBJusCw8WEYrBSJTYGdL78'
    },
    {
      start: 5.2,
      end: 8.4,
      text: 'Collier Flintlock Revolver \u00b7 Artem Goyko \u00b7 Kyiv, Ukraine',
      url: 'https://www.3dway.io/model/J3SkvwPdlwlei8qV7huFR5'
    },
    {
      start: 8.4,
      end: 11.4,
      text: 'Heavy Combat Mech with Pilot \u00b7 NatalieDesign \u00b7 Los Angeles, California',
      url: 'https://www.3dway.io/model/Xwq5sU5zFaEnv3T6qqPzho'
    },
    {
      start: 11.4,
      end: 13.6,
      text: 'Lamborghini \u00b7 B\u1EA3o To\u00E0n \u00b7 Vietnam',
      url: 'https://www.3dway.io/model/JwlZYv5FL4o5um7FBTiEg8'
    }
  ];

  private initMobileHeroAnimation(): void {
    const heroSection = this.elementRef.nativeElement.querySelector('.hero-section') as HTMLElement;
    const heroTitle = this.elementRef.nativeElement.querySelector('.hero-title--mobile') as HTMLElement;
    const heroDescription = this.elementRef.nativeElement.querySelector('.hero-description') as HTMLElement;
    const heroCaption = this.elementRef.nativeElement.querySelector('.hero-media__caption.mobile_caption') as HTMLElement;

    if (!heroTitle || !heroDescription || !heroSection) return;

    this.removeScrollListener();
    if (this.heroUpdateRaf !== null) {
      cancelAnimationFrame(this.heroUpdateRaf);
      this.heroUpdateRaf = null;
    }

    // Only for mobile screens
    if (window.innerWidth > 768) {
      heroTitle.style.position = '';
      heroTitle.style.top = '';
      return;
    }

    const titleTop = 50; // Fixed top position
    const gapBeforeDescription = 29; // Gap between title bottom and description top
    const gapBeforeCaption = 16; // Slightly tighter spacing before media caption

    const updateTitlePosition = () => {
      const descRect = heroDescription.getBoundingClientRect();
      const captionIsVisible = Boolean(heroCaption && heroCaption.offsetParent);
      const captionRect = captionIsVisible ? heroCaption.getBoundingClientRect() : null;
      const sectionRect = heroSection.getBoundingClientRect();
      const titleHeight = heroTitle.offsetHeight;
      const targetTop = captionRect ? Math.min(descRect.top, captionRect.top) : descRect.top;
      const useCaption = captionRect ? captionRect.top <= descRect.top : false;
      const gapBeforeTarget = useCaption ? gapBeforeCaption : gapBeforeDescription;
      const switchPoint = titleTop + titleHeight + gapBeforeTarget;

      if (targetTop <= switchPoint) {
        const absoluteTop = targetTop - sectionRect.top - gapBeforeTarget - titleHeight;
        heroTitle.style.position = 'absolute';
        heroTitle.style.top = `${absoluteTop}px`;
      } else {
        heroTitle.style.position = 'fixed';
        heroTitle.style.top = `${titleTop}px`;
      }
    };

    this.scrollHandler = () => {
      if (this.heroUpdateRaf !== null) return;
      this.heroUpdateRaf = requestAnimationFrame(() => {
        this.heroUpdateRaf = null;
        updateTitlePosition();
      });
    };

    this.addScrollListener();

    // Initial call after layout settles
    this.heroUpdateRaf = requestAnimationFrame(() => {
      this.heroUpdateRaf = null;
      updateTitlePosition();
    });
  }

  private setupHeroResizeHandlers(): void {
    if (this.resizeHandler || this.orientationHandler) return;

    this.resizeHandler = () => {
      this.initMobileHeroAnimation();
      this.setupHeroCaptionSync();
    };

    this.orientationHandler = () => {
      setTimeout(() => {
        this.initMobileHeroAnimation();
        this.setupHeroCaptionSync();
      }, 120);
    };

    window.addEventListener('resize', this.resizeHandler, { passive: true });
    window.addEventListener('orientationchange', this.orientationHandler as any, { passive: true });
  }

  private addScrollListener(): void {
    if (!this.scrollHandler) return;
    if (this.isWindow(this.scrollContainer)) {
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    } else {
      this.scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  private removeScrollListener(): void {
    if (!this.scrollHandler) return;
    if (this.isWindow(this.scrollContainer)) {
      window.removeEventListener('scroll', this.scrollHandler);
    } else {
      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
    }
  }

  private resolveScrollContainer(): Window | HTMLElement {
    const host = this.elementRef.nativeElement as HTMLElement;
    let node: HTMLElement | null = host.parentElement;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return node;
      }
      node = node.parentElement;
    }
    return window;
  }

  private isWindow(target: Window | HTMLElement): target is Window {
    return target === window;
  }

  private playAllVideos(): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    const desktopHero = host.querySelector('.hero-section .monitor-video') as HTMLVideoElement | null;
    const mobileHero = host.querySelector('.hero-section .phone-media') as HTMLVideoElement | null;
    const desktopShowWork = host.querySelector('.show-work-media .monitor-video') as HTMLVideoElement | null;
    const mobileShowWork = host.querySelector('.show-work-media .phone-media') as HTMLVideoElement | null;

    const syncedVideos = new Set<HTMLVideoElement>();

    if (desktopHero && mobileHero) {
      if (window.innerWidth < 768) {
        mobileHero.muted = true;
        mobileHero.playsInline = true;
        mobileHero.play().catch(() => {});
      } else {
        this.playHeroVideosInSync(desktopHero, mobileHero);
      }
      syncedVideos.add(desktopHero);
      syncedVideos.add(mobileHero);
    }

    if (desktopShowWork && mobileShowWork) {
      this.playHeroVideosInSync(desktopShowWork, mobileShowWork);
      syncedVideos.add(desktopShowWork);
      syncedVideos.add(mobileShowWork);
    }

    const videos = Array.from(host.querySelectorAll('video')) as HTMLVideoElement[];
    videos.forEach((video) => {
      if (syncedVideos.has(video)) return;
      video.muted = true;
      video.play().catch(() => {});
    });
  }

  private setupHeroCaptionSync(): void {
    if (this.heroCaptionCleanup) {
      this.heroCaptionCleanup();
      this.heroCaptionCleanup = null;
    }

    const video = this.getHeroCaptionVideo();
    if (!video) return;

    const updateDuration = () => {
      if (Number.isFinite(video.duration)) {
        this.heroVideoDuration = video.duration;
      }
    };

    const updateCaption = () => {
      const time = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      const duration = this.heroVideoDuration;

      if (duration > 0) {
        const inLoopWindow = time >= Math.max(duration - this.heroCaptionLoopLead, 0);
        if (inLoopWindow) {
          if (!this.heroCaptionLooping && this.heroCaptionIndex !== 0) {
            this.heroCaptionLooping = true;
            this.applyCaptionTransition(0, true);
            this.heroCaptionIndex = 0;
          }
          return;
        }
        if (this.heroCaptionLooping && time < this.heroCaptionLoopLead) {
          this.heroCaptionLooping = false;
        }
      }

      const nextIndex = this.heroCaptionCues.findIndex((item) => time >= item.start && time < item.end);
      const resolvedIndex = nextIndex === -1 ? this.heroCaptionCues.length - 1 : nextIndex;

      if (resolvedIndex === this.heroCaptionIndex) return;

      this.applyCaptionTransition(resolvedIndex, true);
    };

    updateCaption();
    updateDuration();
    updateCaption();
    video.addEventListener('timeupdate', updateCaption);
    video.addEventListener('seeked', updateCaption);
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('durationchange', updateDuration);

    this.heroCaptionCleanup = () => {
      video.removeEventListener('timeupdate', updateCaption);
      video.removeEventListener('seeked', updateCaption);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('durationchange', updateDuration);
    };
  }

  private getHeroCaptionVideo(): HTMLVideoElement | null {
    const isMobile = window.innerWidth < 768;
    const mobileVideo =
      this.heroMobileVideo?.nativeElement ||
      (this.elementRef.nativeElement.querySelector('.hero-section .phone-media') as HTMLVideoElement | null);
    const desktopVideo =
      this.heroMonitorVideo?.nativeElement ||
      (this.elementRef.nativeElement.querySelector('.hero-section .monitor-video') as HTMLVideoElement | null);

    return (isMobile ? mobileVideo : desktopVideo) ?? desktopVideo ?? mobileVideo ?? null;
  }

  private applyCaptionTransition(nextIndex: number, animate: boolean): void {
    const nextCue = this.heroCaptionCues[nextIndex];
    const nextText = nextCue.text;
    const nextUrl = nextCue.url;
    const outClass = 'hero-media__caption--slide-out';
    const inClass = 'hero-media__caption--slide-in';

    this.clearCaptionTimers();

    if (!animate) {
      this.heroMediaCaptionClass = '';
      this.heroMediaCaption = nextText;
      this.heroMediaCaptionUrl = nextUrl;
      this.heroCaptionIndex = nextIndex;
      return;
    }

    this.heroMediaCaptionClass = outClass;

    this.heroCaptionTimers.push(
      window.setTimeout(() => {
        this.heroMediaCaption = nextText;
        this.heroMediaCaptionUrl = nextUrl;
        this.heroCaptionIndex = nextIndex;
        this.heroMediaCaptionClass = inClass;
      }, this.heroCaptionHalfDuration)
    );

    this.heroCaptionTimers.push(
      window.setTimeout(() => {
        this.heroMediaCaptionClass = '';
      }, this.heroCaptionFullDuration)
    );
  }

  private clearCaptionTimers(): void {
    this.heroCaptionTimers.forEach((timer) => window.clearTimeout(timer));
    this.heroCaptionTimers = [];
  }

  private playHeroVideosInSync(desktopVideo: HTMLVideoElement, mobileVideo: HTMLVideoElement): void {
    [desktopVideo, mobileVideo].forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      try { video.pause(); } catch {}
    });

    Promise.all([this.waitForVideoReady(desktopVideo), this.waitForVideoReady(mobileVideo)])
      .then(() => {
        try { desktopVideo.currentTime = 0; } catch {}
        try { mobileVideo.currentTime = 0; } catch {}
        requestAnimationFrame(() => {
          desktopVideo.play().catch(() => {});
          mobileVideo.play().catch(() => {});
        });
      });
  }

  private waitForVideoReady(video: HTMLVideoElement): Promise<void> {
    if (video.readyState >= 2) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        resolve();
      };
      const cleanup = () => {
        video.removeEventListener('canplay', onReady);
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('error', onError);
      };
      video.addEventListener('canplay', onReady, { once: true });
      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('error', onError, { once: true });
    });
  }

  get lowerVideoUrl(): string {
    const isMobile = window.innerWidth <= 768;
    return isMobile
      ? `${this.landingMediaBaseUrl}/_landing/lower_video_mobile.mp4`
      : `${this.landingMediaBaseUrl}/_landing/lower_video_desktop.mp4`;
  }

  onSignInClicked(): void {
    this.authModalService.openAuthModal(true, true);
  }

  onTryForFreeClicked(): void {
    this.authModalService.openAuthModal(false, true);
  }

  openYoutubeVideo(): void {
    const isMobile = window.innerWidth <= 768;
    const videoUrl = isMobile
      ? 'https://www.youtube.com/embed/cTz3cAZo3zA'
      : 'https://www.youtube.com/embed/QC4xjn3XloI';
    const isVertical = isMobile; // Mobile uses vertical video
    const { iframeWidth, iframeHeight } = this.utilityService.calculateIframeDimensions(isVertical);

    this.dialog.open(YoutubeDialogComponent, {
      width: iframeWidth,
      height: iframeHeight,
      id: 'youtube-dialog',
      panelClass: isVertical ? 'youtube-dialog-container-vertical' : 'youtube-dialog-container-link',
      data: { videoUrl, isVertical }
    });
  }
}
